import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _excluded = ["id", "name", "checked", "disabled", "onChange", "iconChecked", "iconUnchecked", "dataTestId", "aria-label", "aria-labelledby", "defaultChecked"];

var _templateObject;

import React, { forwardRef, useEffect, useState } from 'react';
import { describe, PropTypes } from '@amzn/stencil-react-desc';
import { styledWithTheme } from "../context";
import { IconCross, IconCheck, IconSize } from "../icons";
import { buildDataTestProps, filterProps } from "../utils";
import Background from "./Background";
import Button from "./Button";
import Container from "./Container";
import { jsx as ___EmotionJSX } from "@emotion/core";
var Checkbox = styledWithTheme('input', {
  shouldForwardProp: filterProps(['defaultChecked', 'error', 'warning'])
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  position: absolute;\n  opacity: 0;\n  height:100%;\n  width:100%;\n  margin:0;\n"])));
var ToggleSwitch = /*#__PURE__*/forwardRef(function (_ref, externalRef) {
  var id = _ref.id,
      name = _ref.name,
      checked = _ref.checked,
      disabled = _ref.disabled,
      onChange = _ref.onChange,
      _ref$iconChecked = _ref.iconChecked,
      iconChecked = _ref$iconChecked === void 0 ? IconCheck : _ref$iconChecked,
      _ref$iconUnchecked = _ref.iconUnchecked,
      iconUnchecked = _ref$iconUnchecked === void 0 ? IconCross : _ref$iconUnchecked,
      dataTestId = _ref.dataTestId,
      ariaLabel = _ref['aria-label'],
      ariaLabelledBy = _ref['aria-labelledby'],
      defaultChecked = _ref.defaultChecked,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(checked !== undefined ? checked : Boolean(defaultChecked)),
      _useState2 = _slicedToArray(_useState, 2),
      isChecked = _useState2[0],
      setIsChecked = _useState2[1];

  var onToggle = function onToggle() {
    if (disabled) {
      return;
    }

    var newValue = !isChecked;
    setIsChecked(newValue);
    !!onChange && onChange(newValue);
  };

  var Icon = isChecked ? iconChecked : iconUnchecked;
  var dataTestProps = buildDataTestProps({
    dataTestComponent: 'StencilToggleSwitch',
    dataTestId: dataTestId
  });
  var inputDataTestProps = buildDataTestProps({
    dataTestComponent: 'StencilToggleSwitchInput',
    dataTestId: dataTestId
  });
  useEffect(function () {
    if (checked !== undefined) {
      setIsChecked(checked);
    }
  }, [checked]);
  return ___EmotionJSX(Container, _extends({}, dataTestProps, {
    "data-test-selected": isChecked,
    isDisabled: !!disabled
  }), ___EmotionJSX(Checkbox, _extends({}, inputDataTestProps, rest, {
    id: id || name,
    name: name,
    role: "switch",
    type: "checkbox",
    disabled: disabled,
    onChange: onToggle,
    checked: isChecked,
    "aria-checked": isChecked,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ref: externalRef
  })), ___EmotionJSX(Background, {
    isDisabled: !!disabled,
    active: isChecked,
    onClick: onToggle
  }, ___EmotionJSX(Button, {
    isDisabled: !!disabled,
    active: isChecked,
    className: "toggle-button"
  }, ___EmotionJSX(Icon, {
    size: IconSize.ExtraSmall,
    color: disabled ? 'neutral50' : 'primary70',
    "aria-hidden": true
  }))));
});
ToggleSwitch.defaultProps = {
  defaultChecked: false,
  disabled: false,
  onChange: function onChange() {
    return undefined;
  },
  iconChecked: IconCheck,
  iconUnchecked: IconCross
};
ToggleSwitch.displayName = 'ToggleSwitch';
var ToggleSwitchWithDocs = describe(ToggleSwitch).description('Toggle element for switching preferences on or off.');
ToggleSwitchWithDocs.propTypes = {
  defaultChecked: PropTypes.bool.description('Sets whether the toggle switch is checked when the page loads.'),
  checked: PropTypes.bool.description('Whether the toggle switch is on or off, when used as a controlled component.'),
  disabled: PropTypes.bool.description('Prevents the user from interacting with the toggle switch.'),
  onChange: PropTypes.func.description('Function called when the switch is turned on or off').format("(newState: bool) => void"),
  id: PropTypes.string.description('Identifier for the toggle switch. Must be unique amongst all the form elements on the page.'),
  name: PropTypes.string.description('Sets the name and id of the switch.'),
  'aria-labelledby': PropTypes.string.description('The id of the label for this element. Used to connect the label with the toggle switch for assistive technologies.'),
  'aria-label': PropTypes.string.description("Sets the 'aria-label' attribute in the DOM for the component."),
  iconChecked: PropTypes.func.description('The icon that will be used for rendering the switch when on.'),
  iconUnchecked: PropTypes.func.description('The icon that will be used for rendering the switch when off.'),
  dataTestId: PropTypes.string.description("Sets the 'data-test-id' attribute in the DOM, for testing purposes.")
};
export default ToggleSwitchWithDocs;